import { createContext, useState, useEffect, useCallback, useContext } from 'react';
import type { ReactNode } from 'react';
import React from 'react';
import { authService, type UserProfile } from '../services/authService';
import { getAccessToken, setAccessToken, setLogoutCallback } from '../services/api';
import { invalidateUserRegionColors } from './useUserRegionColors';
import { useTenantConfig } from './useTenantConfig';

interface AuthContextType {
  user: UserProfile | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  login: (identifier: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isAdmin: false,
  isLoading: true,
  login: async () => {},
  logout: async () => {},
  refreshUser: async () => {},
});

export const useAuth = () => useContext(AuthContext);

const DEV_USER_PROFILE_KEY = 'lm_dev_user_profile';
const isDev = import.meta.env.DEV;
const DEV_PROFILE_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30;

const getDevCookie = (name: string): string | null => {
  if (!isDev || typeof document === 'undefined') {
    return null;
  }

  const cookie = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${name}=`));
  if (!cookie) {
    return null;
  }

  return decodeURIComponent(cookie.split('=').slice(1).join('='));
};

const setDevCookie = (name: string, value: string): void => {
  if (!isDev || typeof document === 'undefined') {
    return;
  }

  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${DEV_PROFILE_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`;
};

const clearDevCookie = (name: string): void => {
  if (!isDev || typeof document === 'undefined') {
    return;
  }

  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`;
};

const getDevAuthStorage = (): Storage | null => {
  if (!isDev || typeof window === 'undefined') {
    return null;
  }

  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const readPersistedDevUser = (): UserProfile | null => {
  const storage = getDevAuthStorage();
  const rawProfile = storage?.getItem(DEV_USER_PROFILE_KEY) || getDevCookie(DEV_USER_PROFILE_KEY);
  if (!rawProfile) {
    return null;
  }

  try {
    const profile = JSON.parse(rawProfile) as UserProfile;
    storage?.setItem(DEV_USER_PROFILE_KEY, rawProfile);
    if (getDevCookie(DEV_USER_PROFILE_KEY) !== rawProfile) {
      setDevCookie(DEV_USER_PROFILE_KEY, rawProfile);
    }
    return profile;
  } catch {
    storage?.removeItem(DEV_USER_PROFILE_KEY);
    clearDevCookie(DEV_USER_PROFILE_KEY);
    return null;
  }
};

const writePersistedDevUser = (profile: UserProfile): void => {
  if (!isDev) {
    return;
  }

  const serializedProfile = JSON.stringify(profile);
  getDevAuthStorage()?.setItem(DEV_USER_PROFILE_KEY, serializedProfile);
  setDevCookie(DEV_USER_PROFILE_KEY, serializedProfile);
};

const clearPersistedDevUser = (): void => {
  getDevAuthStorage()?.removeItem(DEV_USER_PROFILE_KEY);
  clearDevCookie(DEV_USER_PROFILE_KEY);
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const tenant = useTenantConfig();
  const [user, setUser] = useState<UserProfile | null>(() => (
    getAccessToken() ? readPersistedDevUser() : null
  ));
  const [isLoading, setIsLoading] = useState(() => !(getAccessToken() && readPersistedDevUser()));

  const isAuthenticated = user !== null || (isDev && getAccessToken() !== null);
  const isAdmin = user?.role === 'admin' || user?.role === 'superuser';

  const logout = useCallback(async () => {
    await authService.logout();
    invalidateUserRegionColors();
    clearPersistedDevUser();
    setUser(null);
  }, []);

  useEffect(() => {
    setLogoutCallback(() => {
      invalidateUserRegionColors();
      clearPersistedDevUser();
      setUser(null);
    });
  }, []);

  useEffect(() => {
    const restoreSession = async () => {
      const cachedDevUser = readPersistedDevUser();
      try {
        // If we already have an access token (hydrated from local dev storage),
        // try the profile directly. The api interceptor auto-refreshes on 401.
        if (getAccessToken()) {
          if (cachedDevUser) {
            setUser(cachedDevUser);
          }
          const profile = await authService.getUserProfile();
          writePersistedDevUser(profile);
          setUser(profile);
          return;
        }
        const token = await authService.refreshToken();
        if (token) {
          const profile = await authService.getUserProfile();
          writePersistedDevUser(profile);
          setUser(profile);
        }
      } catch {
        if (!isDev || !cachedDevUser || !getAccessToken()) {
          setAccessToken(null);
          clearPersistedDevUser();
          setUser(null);
        }
      } finally {
        setIsLoading(false);
      }
    };

    restoreSession();
  }, []);

  const login = useCallback(async (identifier: string, password: string) => {
    const response = await authService.login(
      identifier,
      password,
      tenant.authentication?.mode ?? 'email',
    );
    writePersistedDevUser(response.user);
    setUser(response.user);
  }, [tenant.authentication?.mode]);

  const refreshUser = useCallback(async () => {
    const profile = await authService.getUserProfile();
    writePersistedDevUser(profile);
    setUser(profile);
  }, []);

  return React.createElement(
    AuthContext.Provider,
    { value: { user, isAuthenticated, isAdmin, isLoading, login, logout, refreshUser } },
    children,
  );
};
